import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Plus, Trash2, Clock, Send, Loader2, CheckCircle2, AlertCircle, Image as ImageIcon, Film } from 'lucide-react';
import PageHeader, { AccountSelector } from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api, { apiError, mediaUrl, fmtDateTime } from '../../lib/api';
import CanalPosts from './CanalPosts';
import CanalCalendrier from './CanalCalendrier';
import { srcMedia } from '../../lib/canalPosts';

const PERIODS = [[21600, '6 h'], [43200, '12 h'], [86400, '24 h'], [172800, '48 h']];
const STATUS = {
  pending: ['bg-amber-500/15 text-amber-300 border-amber-500/30', 'Programmée'],
  posted: ['bg-emerald-500/15 text-emerald-300 border-emerald-500/30', 'Postée'],
  failed: ['bg-rose-500/15 text-rose-300 border-rose-500/30', 'Échec'],
  canceled: ['bg-slate-500/15 text-slate-400 border-slate-500/30', 'Annulée'],
};

// Heure locale → valeur d'<input type=datetime-local> (par défaut maintenant + 1 h).
const defaultWhen = () => {
  const d = new Date(Date.now() + 3600000);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

// 26/09 (#282) : borne basse du selecteur de date. Deux posts sont partis en pleine nuit
// parce que le mois saisi etait septembre au lieu d octobre — une date passee vaut
// « publie tout de suite ». Le navigateur refuse desormais la date sur place, et le
// serveur la refuse aussi (garde-fou des deux cotes).
const maintenantPourInput = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export default function Stories() {
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState('');
  const [rows, setRows] = useState([]);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [period, setPeriod] = useState(86400);
  const [when, setWhen] = useState(defaultWhen());
  const [saving, setSaving] = useState(false);
  const [target, setTarget] = useState('account'); // 'account' = profil modèle · 'channel' = canal
  const [channels, setChannels] = useState([]);
  const [channel, setChannel] = useState('');
  const fileRef = useRef(null);
  const [onglet, setOnglet] = useState('story');   // 13/09 : une seule section « Canal » avec Story / Post

  // Canaux où le compte est admin (mêmes que les tracking links) → cibles possibles.
  useEffect(() => {
    if (!account || target !== 'channel') return;
    api.get('/tracking/channels', { params: { account_id: account } })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : (data.channels || []);
        setChannels(list);
        setChannel(prev => (list.some(x => x.id === prev) ? prev : (list[0]?.id || '')));
      }).catch(() => setChannels([]));
  }, [account, target]);

  useEffect(() => {
    api.get('/accounts').then(({ data }) => {
      const ready = data.filter(a => a.status === 'ready');
      setAccounts(ready);
      if (ready[0]) setAccount(ready[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!account) { setRows([]); return; }
    api.get('/stories', { params: { account_id: account } }).then(({ data }) => setRows(data || [])).catch(() => {});
  }, [account]);
  useEffect(() => { load(); }, [load]);
  // Rafraîchit pour voir les stories passer de « programmée » à « postée ».
  useEffect(() => { const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const pickFile = (f) => {
    setFile(f || null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(f ? URL.createObjectURL(f) : '');
  };

  // 17/09 (Marlon) : glisser-déposer la photo / vidéo de la story, comme pour les posts du canal.
  const [survol, setSurvol] = useState(false);
  const survolCpt = useRef(0);
  const estMedia = (f) => /^(image|video)\//.test(f?.type || '') || /\.(jpe?g|png|webp|gif|heic|mp4|mov|m4v|webm)$/i.test(f?.name || '');
  const prendreDepose = (fichiers) => {
    const liste = Array.from(fichiers || []);
    const f = liste.find(estMedia);
    if (!f) { if (liste.length) toast.error('Photo ou vidéo seulement'); return; }
    if (liste.length > 1) toast.info('Une seule photo ou vidéo par story : j\'ai pris la première.');
    pickFile(f);
    if (fileRef.current) fileRef.current.value = '';
  };
  const avecFichiers = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const glisser = {
    onDragEnter: (e) => { if (!avecFichiers(e)) return; e.preventDefault(); survolCpt.current += 1; setSurvol(true); },
    onDragOver: (e) => { if (!avecFichiers(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; },
    onDragLeave: (e) => { if (!avecFichiers(e)) return; survolCpt.current = Math.max(0, survolCpt.current - 1); if (!survolCpt.current) setSurvol(false); },
    onDrop: (e) => {
      if (!avecFichiers(e)) return;
      e.preventDefault(); survolCpt.current = 0; setSurvol(false);
      prendreDepose(e.dataTransfer.files);
    },
  };
  // Un fichier lâché à côté de la zone ne doit pas ouvrir la photo dans le navigateur (on quitterait la page).
  const horsZone = {
    onDragOver: (e) => { if (avecFichiers(e)) e.preventDefault(); },
    onDrop: (e) => { if (avecFichiers(e)) e.preventDefault(); },
  };

  const submit = async (now = false) => {
    if (!account) { toast.error('Choisis un modèle'); return; }
    if (!file) { toast.error('Ajoute une photo ou une vidéo'); return; }
    if (target === 'channel' && !channel) { toast.error('Choisis un canal'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('account_id', account);
      fd.append('caption', caption);
      fd.append('period', String(period));
      fd.append('scheduled_at', now ? new Date().toISOString() : new Date(when).toISOString());
      if (target === 'channel') {
        fd.append('channel_id', channel);
        fd.append('channel_name', (channels.find(c => c.id === channel) || {}).title || '');
      }
      await api.post('/stories/schedule', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
      toast.success(now ? 'Story envoyée — postée dans ~30 s' : 'Story programmée');
      pickFile(null); setCaption(''); if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (e) { toast.error(apiError(e, 'Échec de la programmation')); }
    finally { setSaving(false); }
  };

  const cancel = async (id) => {
    try { await api.delete(`/stories/${id}`); setRows(prev => prev.map(r => r.id === id ? { ...r, status: 'canceled' } : r)); }
    catch (e) { toast.error(apiError(e, 'Échec')); }
  };
  const postNow = async (id) => {
    try { await api.post(`/stories/${id}/post-now`); toast.success('La story part dans ~30 s'); load(); }
    catch (e) { toast.error(apiError(e, 'Échec')); }
  };

  const isVid = file && file.type.startsWith('video');

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto fade-in" data-testid="stories-page" {...(onglet === 'story' ? horsZone : {})}>
      <PageHeader
        title="Canal"
        subtitle={onglet === 'post'
          ? 'Posts du canal avec bouton — publiés par le bot du canal à l\'heure prévue (24/7)'
          : onglet === 'calendrier'
            ? 'Stories et posts prévus jour par jour — par modèle, à l\'heure de Paris'
            : 'Programme les stories du profil ou du canal — postées toutes seules à l\'heure prévue (24/7)'}
        actions={onglet === 'story' ? <AccountSelector accounts={accounts} value={account} onChange={setAccount} /> : null}
      />

      <div className="flex gap-1 p-1 rounded-lg bg-white/5 border border-white/10 w-fit mb-4">
        {[['story', 'Story'], ['post', 'Post'], ['calendrier', '📅 Vue calendrier']].map(([v, l]) => (
          <button key={v} type="button" onClick={() => setOnglet(v)} data-testid={`canal-onglet-${v}`}
            className={`px-4 h-8 rounded-md text-sm font-medium transition-colors ${onglet === v ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'}`}>{l}</button>
        ))}
      </div>

      {onglet === 'post' ? <CanalPosts /> : onglet === 'calendrier' ? <CanalCalendrier /> : (<>
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Composer */}
        <div {...glisser} data-testid="story-form" data-survol={survol ? '1' : '0'}
          className={`rounded-2xl border bg-white/[0.02] p-5 space-y-4 transition-colors ${survol ? 'border-indigo-400/60 ring-2 ring-indigo-400/30' : 'border-white/10'}`}>
          <div className="text-sm font-semibold text-white">Nouvelle story</div>

          <div className="flex gap-1 p-1 rounded-lg bg-white/5 border border-white/10 w-fit">
            {[['account', 'Profil du compte'], ['channel', 'Canal']].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setTarget(v)} data-testid={`story-target-${v}`}
                className={`px-3 h-7 rounded-md text-xs font-medium transition-colors ${target === v ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'}`}>{l}</button>
            ))}
          </div>
          {target === 'channel' && (
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Canal</label>
              <select value={channel} onChange={e => setChannel(e.target.value)} data-testid="story-channel"
                className="block mt-1 w-full bg-[#18171C] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none">
                {channels.length === 0 && <option value="">Aucun canal (le compte doit être admin)</option>}
                {channels.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
              <div className="text-[11px] text-amber-300/80 mt-1">⚠️ Le canal doit être éligible aux stories (assez de boosts Telegram), sinon Telegram refuse.</div>
            </div>
          )}

          <div data-testid="story-zone" onClick={() => fileRef.current?.click()} className={`cursor-pointer rounded-xl border border-dashed transition-colors h-56 flex items-center justify-center overflow-hidden ${survol ? 'border-indigo-400 bg-indigo-500/10' : 'border-white/15 bg-white/[0.02] hover:border-indigo-500/40'}`}>
            {survol && <div className="text-center text-indigo-200 text-sm"><Plus className="mx-auto mb-1" size={22} /> Dépose ici</div>}
            {!survol && !file && <div className="text-center text-slate-500 text-sm"><Plus className="mx-auto mb-1" size={22} /> Ajouter une photo / vidéo<div className="text-[11px] text-slate-600 mt-1">ou glisse-la ici</div></div>}
            {!survol && file && !isVid && <img src={previewUrl} alt="" className="h-full object-contain" />}
            {!survol && file && isVid && <video src={previewUrl} className="h-full object-contain" muted controls />}
          </div>
          <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={e => pickFile(e.target.files?.[0])} data-testid="story-file" />

          <textarea value={caption} onChange={e => setCaption(e.target.value)} placeholder="Légende (optionnel)…" rows={2}
            className="w-full bg-[#18171C] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none resize-none placeholder:text-slate-600" />

          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[140px]">
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Disparaît après</label>
              <select value={period} onChange={e => setPeriod(Number(e.target.value))} className="block mt-1 w-full bg-[#18171C] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none">
                {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Date & heure</label>
              <input type="datetime-local" min={maintenantPourInput()} value={when} onChange={e => setWhen(e.target.value)} data-testid="story-when"
                className="block mt-1 w-full bg-[#18171C] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none" />
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <Button data-testid="story-schedule" onClick={() => submit(false)} disabled={saving} className="flex-1 bg-indigo-500 hover:bg-indigo-600">
              {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Clock size={15} className="mr-1.5" />} Programmer
            </Button>
            <Button variant="outline" onClick={() => submit(true)} disabled={saving} className="border-white/10 bg-white/5 text-slate-200 hover:bg-white/10">
              <Send size={14} className="mr-1.5" /> Poster maintenant
            </Button>
          </div>
        </div>

        {/* Queue */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <div className="px-5 py-3 border-b border-white/5 text-sm font-semibold text-white">File des stories</div>
          {rows.length === 0 && <div className="px-5 py-12 text-center text-sm text-slate-500">Aucune story pour ce modèle.</div>}
          <div className="divide-y divide-white/5 max-h-[560px] overflow-y-auto">
            {rows.map(r => {
              const [cls, label] = STATUS[r.status] || STATUS.pending;
              return (
                <div key={r.id} className="flex items-center gap-3 px-4 py-3" data-testid={`story-row-${r.id}`}>
                  <div className="relative w-12 h-12 rounded-lg bg-black/40 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
                    {/* 17/09 : vignette légère (320 px) si le serveur l'a créée, sinon l'original comme avant */}
                    {r.vignette_url
                      ? <img src={srcMedia(r.vignette_url, process.env.REACT_APP_BACKEND_URL)} alt="" loading="lazy" data-testid={`story-vignette-${r.id}`} className="w-full h-full object-cover" />
                      : (r.is_video ? <Film size={16} className="text-slate-400" /> : <img src={mediaUrl(r.url)} alt="" loading="lazy" className="w-full h-full object-cover" />)}
                    {r.is_video && r.vignette_url && <span className="absolute inset-0 flex items-center justify-center bg-black/25 text-white"><Film size={13} /></span>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] text-white truncate">{r.caption || <span className="text-slate-500 italic">sans légende</span>}</div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                      <span className="text-slate-400">{r.channel_id ? `📢 ${r.channel_name || 'Canal'}` : '👤 Profil'}</span>
                      · <Clock size={11} /> {fmtDateTime(r.scheduled_at)}
                      {r.error && <span className="text-rose-400/80 truncate"> · {r.error}</span>}
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${cls} shrink-0`}>
                    {r.status === 'posted' && <CheckCircle2 size={9} className="inline mr-1" />}
                    {r.status === 'failed' && <AlertCircle size={9} className="inline mr-1" />}
                    {label}
                  </span>
                  {r.status === 'pending' && (
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => postNow(r.id)} title="Poster maintenant" className="text-slate-400 hover:text-emerald-300 p-1"><Send size={14} /></button>
                      <button onClick={() => cancel(r.id)} title="Annuler" className="text-slate-400 hover:text-rose-400 p-1"><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 text-[12px] text-slate-400 rounded-xl border border-sky-500/15 bg-sky-500/[0.04] px-4 py-3">
        <ImageIcon size={15} className="text-sky-400 shrink-0 mt-0.5" />
        <span>La story est postée sur le <span className="text-white">profil du compte</span> (la modèle). ChatHub tourne 24/7, donc pas besoin que ton PC soit allumé. <span className="text-slate-500">Les stories de canal arriveront ensuite (canaux boostés uniquement).</span></span>
      </div>
      </>)}
    </div>
  );
}
